var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import EditIcon from '@material-ui/icons/Edit';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useField } from 'react-final-form';
import { Element } from 'react-scroll';
import { LoadingSpinner } from '@/container/Ui/Loading';
import { propBool, propFunc, propStr } from '@/helpers/custom-prop-types';
import { useBoolState } from '@/hooks/state/useBoolState';
import { ACCEPT_IMAGE_MIME_TYPE } from '@/utils/validate/conditions';
import Image from '../../../../../../Ui/Common/Image';
import InputError from '../../../../../../Ui/Common/InputError';
import SeminarOneFormFieldAnyLabel from '../Parts/SeminarOneFormFieldAnyLabel';
import SeminarOneFormFileName from '../Parts/SeminarOneFormFileName';
/**
 * セミナーのサムネイル画像をアップロードするコンポーネント
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const SeminarOneFormThumbnailFileField = (props) => {
    const { className, disabled, iconClassName, name, nameOfFileId, nameOfFileName, nameOfFilePath, nameOfNewFileId, onChangeFileInput, validate, } = props;
    // ファイル本体のフィールド
    const inputRef = useRef();
    const { input, meta } = useField(name, { validate });
    const { onBlur: onBlurInput, onChange: onChangeInput } = input;
    const hasError = meta.touched && meta.error;
    // アップロード用ファイルIDのフィールド
    const { input: { onChange: onChangeNewFiledId }, } = useField(nameOfNewFileId);
    const { input: { onChange: onChangeFileIdInput }, } = useField(nameOfFileId);
    const { input: fileNameInput } = useField(nameOfFileName);
    const { onChange: onChangeFileNameInput } = fileNameInput;
    const { input: filePathInput } = useField(nameOfFilePath);
    const { onChange: onChangeFilePathInput } = filePathInput;
    const [updateFileValue, changeUpdateFileValue] = useState({
        name: '',
        path: '',
    });
    /**
     * ファイル削除時のイベント
     * @type {(function(*): Promise<void>)|*}
     */
    const onDeleteFile = useCallback((e) => {
        // 同じ画像を再度アップした場合の対応
        onChangeInput(null);
        onBlurInput(e);
        changeUpdateFileValue({ name: '', path: '' });
        onChangeFileIdInput(null);
        onChangeFileNameInput(null);
        onChangeFilePathInput(null);
        inputRef.current.value = '';
    }, [
        onChangeInput,
        onBlurInput,
        changeUpdateFileValue,
        inputRef,
        onChangeFileIdInput,
        onChangeFileNameInput,
        onChangeFilePathInput,
    ]);
    /**
     * ファイル変更時のイベント
     * @type {(function(*): void)|*}
     */
    const onChangeFile = useCallback((e) => {
        const uploadFile = e.currentTarget.files[0];
        if (uploadFile) {
            onChangeInput(uploadFile);
            onBlurInput(e);
        }
    }, [onChangeInput, onBlurInput]);
    const { boolState: isUploading, changeFalse: changeUploadingFalse, changeTrue: changeUploadingTrue, } = useBoolState();
    // ファイルの選択状態が変更されたら
    useEffect(() => {
        const effectUnmount = { current: false };
        let url;
        // ファイルが存在しない or エラー表示中の場合は何もしない
        const targetFile = input.value;
        if (targetFile) {
            // ファイルが存在する場合のみ処理を行う
            // 表示用のURLの作成
            url = window.URL.createObjectURL(targetFile);
            (() => __awaiter(void 0, void 0, void 0, function* () {
                const isShowError = !!(meta.touched && meta.error);
                if (isShowError) {
                    return;
                }
                changeUploadingTrue();
                // api呼び出し
                const result = yield onChangeFileInput(targetFile, 'image');
                if (!effectUnmount.current) {
                    changeUploadingFalse();
                    if (result === null || result === void 0 ? void 0 : result.id) {
                        changeUpdateFileValue({
                            name: targetFile.name,
                            path: url,
                        });
                        onChangeNewFiledId(result === null || result === void 0 ? void 0 : result.id);
                    }
                }
            }))();
        }
        else {
            changeUpdateFileValue({
                name: '',
                path: '',
            });
        }
        return () => {
            effectUnmount.current = true;
            if (url) {
                window.URL.revokeObjectURL(url);
            }
        };
    }, [
        onChangeFileInput,
        onChangeNewFiledId,
        input.value,
        meta.touched,
        meta.error,
        changeUploadingFalse,
        changeUploadingTrue,
        changeUpdateFileValue,
    ]);
    const showFileName = updateFileValue.name || fileNameInput.value;
    const showFilePath = updateFileValue.path || filePathInput.value;
    return (_jsxs(Element, Object.assign({ className: className, name: `${name}_view` }, { children: [_jsxs("label", Object.assign({ className: clsx('professional_seminar_one_form_thumbnail', hasError && 'is-warning', !disabled && 'pointer') }, { children: [_jsx("div", Object.assign({ className: 'thumbnail_inner' }, { children: (() => {
                            if (isUploading) {
                                return (_jsx("div", Object.assign({ className: 'thumbnail_empty' }, { children: _jsx(LoadingSpinner, {}) })));
                            }
                            if (showFilePath) {
                                return (_jsx("div", Object.assign({ className: 'thumbnail_preview' }, { children: _jsx(Image, { alt: '', className: 'preview_image', isObjectFitContain: true, src: showFilePath, useImage: true }) })));
                            }
                            return (_jsxs("div", Object.assign({ className: 'thumbnail_empty' }, { children: [_jsxs("div", Object.assign({ className: 'empty_title' }, { children: [_jsx("div", Object.assign({ className: 'empty_text' }, { children: "\u30B5\u30E0\u30CD\u30A4\u30EB" })), _jsx(SeminarOneFormFieldAnyLabel, { className: 'empty_label' })] })), _jsxs("div", Object.assign({ className: 'empty_description' }, { children: [_jsx("div", { children: "PNG\u307E\u305F\u306FJPEG\u306E\u307F" }), _jsx("div", Object.assign({ className: 'text-liststyle' }, { children: "10MB\u307E\u3067\u306E\u30D5\u30A1\u30A4\u30EB\u3092\u63A8\u5968" })), _jsx("div", Object.assign({ className: 'text-liststyle' }, { children: "\u63A8\u5968\u30B5\u30A4\u30BA\uFF1A400\u00D7400" }))] }))] })));
                        })() })), _jsx("input", { accept: ACCEPT_IMAGE_MIME_TYPE, className: 'is-dnone', disabled: disabled, onChange: onChangeFile, onFocus: input.onFocus, ref: inputRef, type: 'file' }), !disabled && (_jsx(EditIcon, { className: clsx('thumbnail_icon', iconClassName) }))] })), showFileName && showFilePath && (_jsx(SeminarOneFormFileName, { className: 'professional_seminar_one_form_thumbnail_delete', disabled: isUploading || disabled, fileName: showFileName, filePath: showFilePath, onClickDelete: onDeleteFile })), _jsx(InputError, { error: hasError ? meta.error : '' })] })));
};
SeminarOneFormThumbnailFileField.propTypes = {
    className: propStr(),
    disabled: propBool().isRequired,
    iconClassName: propStr(),
    name: propStr().isRequired,
    nameOfFileId: propStr().isRequired,
    nameOfFileName: propStr().isRequired,
    nameOfFilePath: propStr().isRequired,
    nameOfNewFileId: propStr().isRequired,
    onChangeFileInput: propFunc().isRequired,
    validate: propFunc().isRequired,
};
SeminarOneFormThumbnailFileField.defaultProps = {
    className: '',
    iconClassName: '',
};
export default SeminarOneFormThumbnailFileField;
